/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";

// Custom styles for the SidenavItem
import { item, itemContent, itemArrow } from "examples/Sidenav/styles/sidenavItem";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

// Import system theme hook
import { useSystemTheme } from "context/SystemThemeContext";

function SidenavItem({ name, active = false, nested = false, children, open = false, ...rest }) {
  const { theme } = useSystemTheme(); // Get current theme mode
  const [controller] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const [collapseOpen, setCollapseOpen] = useState(open);

  const handleClick = () => {
    if (children) {
      setCollapseOpen(!collapseOpen);
    }
  };

  return (
    <>
      <ListItem component="li" sx={item}>
        <SoftBox
          {...rest}
          onClick={handleClick}
          sx={(themeCallback) => {
            const styles = itemContent(themeCallback, { 
              active, 
              miniSidenav, 
              name, 
              nested 
            });
            return {
              ...styles,
              "& span": {
                ...styles["& span"],
                color: active 
                  ? themeCallback.palette.primary.main 
                  : theme.palette.mode === "dark"
                    ? themeCallback.palette.text.primary
                    : styles["& span"]?.color,
              },
              "&::before": {
                ...styles["&::before"],
                backgroundColor: active 
                  ? themeCallback.palette.secondary.main 
                  : theme.palette.mode === "dark"
                    ? themeCallback.palette.text.main
                    : styles["&::before"]?.backgroundColor,
                color: active 
                  ? themeCallback.palette.primary.main 
                  : theme.palette.mode === "dark"
                    ? themeCallback.palette.text.main
                    : styles["&::before"]?.color,
              },
            };
          }}
        >
          {!nested && (
            <ListItemIcon>
              <Icon>article</Icon>
            </ListItemIcon>
          )}
          <ListItemText primary={name} />
          {children && (
            <Icon
              sx={(themeCallback) => {
                const styles = itemArrow(themeCallback, { open: collapseOpen, miniSidenav });
                return {
                  ...styles,
                  color: collapseOpen 
                    ? themeCallback.palette.primary.main 
                    : theme.palette.mode === "dark"
                      ? themeCallback.palette.text.secondary
                      : styles.color,
                  transform: collapseOpen ? "rotate(0)" : "rotate(-180deg)",
                };
              }}
            >
              expand_less
            </Icon>
          )}
        </SoftBox>
      </ListItem>
      {children && (
        <Collapse in={collapseOpen} unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

// Typechecking props for the SidenavItem
SidenavItem.propTypes = {
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  nested: PropTypes.bool,
  children: PropTypes.node,
  open: PropTypes.bool,
};

export default SidenavItem;